import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import {
  IStock,
  IHistoryStock,
  useCountriesQuery,
  useCurrenciesQuery,
  useCompaniesNamesQuery,
  useSectorsQuery,
  useInvestmentFocusesQuery,
  useUpdateStockMutation,
  IStockInput,
  ICompany,
  ICountry,
  ICurrency,
  IInvestmentFocus,
  ISector
} from '../../generated/graphql';
import { Box, FormControl, FormControlLabel, Grid, Switch } from '@mui/material';
import InputComponent from '../Common/Fields/InputComponent';
import {
  getObjectInputId,
  getStringInput,
  parseDate,
  stockCodeExists
} from '../../utils/clientUtils';
import { Actions } from '../../enums/ActionEnums';
import { IErrors, errorTexts } from './StockForm';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';

interface IProps {
  stock: IStock | null;
  editable: boolean;
  saveTriggered: number;
  refreshStock: (input: IStock) => void;
  historyStock: IHistoryStock;
}

const defaultErrorsObj: IErrors = {
  stockCode: null,
  stockName: null,
  company: null,
  inclusion: null
};

const StockDetails: React.FunctionComponent<IProps> = ({
  stock,
  editable,
  saveTriggered,
  refreshStock,
  historyStock
}: IProps) => {
  const { dispatch } = useContext(AppContext);
  const countries = useCountriesQuery();
  const currencies = useCurrenciesQuery();
  const companies = useCompaniesNamesQuery();
  const sectors = useSectorsQuery();
  const investmentFocuses = useInvestmentFocusesQuery();
  const [stockDraft, setStockDraft] = useState<IStock | null>(stock);
  const [errors, setErrors] = useState(defaultErrorsObj);
  const [updateStock] = useUpdateStockMutation();

  useEffect(() => {
    setStockDraft(stock);
    setErrors(defaultErrorsObj);
  }, [stock, editable]);

  useEffect(() => {
    if (saveTriggered > 0 && isValidStock()) {
      const stockToUpdate = stockDraft as IStock;
      const input: IStockInput = {
        name: stockToUpdate.name,
        company_id: parseInt(stockToUpdate.company?.id as string),
        is_reit: stockToUpdate.is_reit,
        start_date: (parseDate(stockToUpdate.start_date) as Date).toString(),
        bloomberg_ticker: getStringInput(stockToUpdate.bloomberg_ticker),
        cusip: getStringInput(stockToUpdate.cusip),
        country_id: getObjectInputId(stockToUpdate.country?.id),
        currency_id: getObjectInputId(stockToUpdate.currency?.id),
        isin_code: getStringInput(stockToUpdate.isin_code),
        reuters_code: getStringInput(stockToUpdate.reuters_code),
        code: getStringInput(stockToUpdate.code),
        exchange_code: getStringInput(stockToUpdate.exchange_code),
        sedol: getStringInput(stockToUpdate.sedol),
        snl_code: getStringInput(stockToUpdate.snl_code),
        investment_focus_id: getObjectInputId(stockToUpdate.investment_focus?.id),
        icb_industry_code: stockToUpdate.icb_industry_code,
        icb_sector_code: stockToUpdate.icb_sector_code,
        icb_subsector_code: stockToUpdate.icb_subsector_code,
        icb_supersector_code: stockToUpdate.icb_supersector_code,
        sector_id: getObjectInputId(stockToUpdate.sector?.id),
        subsector_id: getObjectInputId(stockToUpdate.subsector?.id)
      };
      const result = {
        ...stockToUpdate,
        historyStock
      } as IStock;
      updateStock({
        variables: {
          id: stock?.id as string,
          input
        },
        optimisticResponse: {
          result
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'success',
          text: `${result.name} updated`
        }
      });
      refreshStock(result);
    }
  }, [saveTriggered]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setErrors({
      ...errors,
      stockName: value.trim().length < 2 ? errorTexts.stockName : null
    });
    setStockDraft({ ...(stockDraft as IStock), name: value.trimStart() });
  };

  const handleStockCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    if (value.length > 0) {
      if (value[0].toLowerCase() === 'c') {
        setErrors({ ...errors, stockCode: null });
      } else {
        setErrors({ ...errors, stockCode: errorTexts.stockCode });
      }
    } else {
      setErrors({ ...errors, stockCode: null });
    }
    setStockDraft({ ...(stockDraft as IStock), code: value.trimStart() });
  };

  const handleCompanyChange = (_: unknown, newValue: React.SetStateAction<ICompany | null>) => {
    if (newValue) {
      setErrors({ ...errors, company: null });
    } else {
      setErrors({ ...errors, company: errorTexts.company });
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    setStockDraft({ ...(stockDraft as IStock), company: newValue });
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      if (!Number.isNaN(newValue.getDate())) {
        setErrors({ ...errors, inclusion: null });
      }
    } else {
      setErrors({ ...errors, inclusion: errorTexts.inclusion });
    }
    setStockDraft({ ...(stockDraft as IStock), start_date: newValue?.getTime().toString() || '' });
  };

  const isValidStock = () => {
    let valid = true;
    const stockToChack = stockDraft as IStock;
    const errors: IErrors = {
      company: null,
      stockCode: null,
      stockName: null,
      inclusion: null
    };

    if (stockToChack.code && stockToChack.code?.length > 0) {
      if (stockToChack.code[0].toLowerCase() !== 'c') {
        errors.stockCode = errorTexts.stockCode;
        valid = false;
      } else {
        const codeExist = stockCodeExists(
          stockToChack.code as string,
          parseInt(stock?.id as string)
        );
        if (codeExist) {
          errors.stockCode = errorTexts.stockCodeExists;
          valid = false;
        }
      }
    }

    if (stockToChack.name.trim().length < 2) {
      valid = false;
      errors.stockName = errorTexts.stockName;
    }
    if (stockToChack.company === null) {
      valid = false;
      errors.company = errorTexts.company;
    }
    if (stockToChack.start_date === null) {
      valid = false;
      errors.inclusion = errorTexts.inclusion;
    }
    setErrors(errors);
    return valid;
  };

  return (
    <Grid container sx={{ mt: '35px', paddingX: '10px' }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <InputComponent
            id={'stock-name'}
            data-testid={'stock-name'}
            value={stockDraft?.name || ''}
            onChange={handleNameChange}
            errorMsg={errors.stockName}
            label={'Stock Name'}
            disabled={!editable}
            isWhite
          />
        </Grid>
        <Grid item xs={2}>
          <InputComponent
            id={'stock-code'}
            data-testid={'stock-code'}
            value={stockDraft?.code || ''}
            onChange={handleStockCodeChange}
            errorMsg={errors.stockCode}
            label={'Stock Code'}
            disabled={!editable}
            isWhite
          />
        </Grid>
        <Grid item xs={2}>
          <InputComponent
            id={'isin-code'}
            data-testid={'isin-code'}
            value={stockDraft?.isin_code || ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), isin_code: e.currentTarget.value })
            }
            disabled={!editable}
            label={'ISIN Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <InputComponent
            id={'sedol'}
            data-testid={'sedol'}
            value={stockDraft?.sedol ?? ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), sedol: e.currentTarget.value })
            }
            disabled={!editable}
            label={'SEDOL'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <InputComponent
            id={'cusip'}
            data-testid={'cusip'}
            value={stockDraft?.cusip ?? ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), cusip: e.currentTarget.value })
            }
            disabled={!editable}
            label={'CUSIP'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <AutocompleteComponent
            id="country-autocomplete"
            data-testid="country-autocomplete"
            options={(countries.data?.result as never[]) || []}
            getOptionLabel={(option: ICountry) => option?.code ?? ''}
            values={stockDraft?.country || null}
            onChange={(_, newValue) =>
              setStockDraft({ ...(stockDraft as IStock), country: newValue })
            }
            disabled={!editable}
            label={'Country Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <AutocompleteComponent
            id="currency-autocomplete"
            data-testid="currency-autocomplete"
            options={(currencies.data?.result as never[]) || []}
            getOptionLabel={(option: ICurrency) => option?.code}
            values={stockDraft?.currency || null}
            onChange={(_, newValue) =>
              setStockDraft({ ...(stockDraft as IStock), currency: newValue })
            }
            disabled={!editable}
            label={'Currency Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <InputComponent
            id={'exchange-code'}
            data-testid={'exchange-code'}
            value={stockDraft?.exchange_code || ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), exchange_code: e.currentTarget.value })
            }
            disabled={!editable}
            label={'Exchange Code'}
            isWhite
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ paddingTop: 35 }}>
        <Grid item xs={1}>
          <AutocompleteComponent
            id="investment-focus-autocomplete"
            data-testid="investment-focus-autocomplete"
            options={(investmentFocuses.data?.result as never[]) || []}
            getOptionLabel={(option: IInvestmentFocus) => option?.name ?? ''}
            values={stockDraft?.investment_focus || null}
            onChange={(_, newValue) =>
              setStockDraft({ ...(stockDraft as IStock), investment_focus: newValue })
            }
            label={'Investment Focus'}
            disabled={!editable}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="start-date-picker"
              data-testid="start-date-picker"
              value={parseDate(stockDraft?.start_date)}
              onChange={(d) => handleDateChange(d)}
              label="Index Inclusion"
              disabled={!editable}
              isWhite
              hideEndIcon
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={1}>
          <InputComponent
            id={'reuters-code'}
            data-testid={'reuters-code'}
            value={stockDraft?.reuters_code ?? ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), reuters_code: e.currentTarget.value })
            }
            disabled={!editable}
            label={'Reuters Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={2}>
          <InputComponent
            id={'icb-sector-code'}
            data-testid={'icb-sector-code'}
            type="number"
            value={stockDraft?.icb_sector_code || ''}
            onChange={(e) =>
              setStockDraft({
                ...(stockDraft as IStock),
                icb_sector_code: parseInt(e.currentTarget.value)
              })
            }
            disabled={!editable}
            label={'ICB Sector Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={2}>
          <InputComponent
            id={'icb-subsector-code'}
            data-testid={'icb-subsector-code'}
            type="number"
            value={stockDraft?.icb_subsector_code || ''}
            onChange={(e) =>
              setStockDraft({
                ...(stockDraft as IStock),
                icb_subsector_code: parseInt(e.currentTarget.value)
              })
            }
            disabled={!editable}
            label={'ICB Subsector Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={3}>
          <AutocompleteComponent
            id="sector-autocomplete"
            data-testid="sector-autocomplete"
            options={(sectors.data?.result as never[]) || []}
            getOptionLabel={(option: ISector) => option?.name}
            values={stockDraft?.sector || null}
            onChange={(_, newValue) =>
              setStockDraft({ ...(stockDraft as IStock), sector: newValue })
            }
            disabled={!editable}
            label={'Sector'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <InputComponent
            id={'bloomberg-ticker'}
            data-testid={'bloomberg-ticker'}
            value={stockDraft?.bloomberg_ticker || ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), bloomberg_ticker: e.currentTarget.value })
            }
            disabled={!editable}
            label={'Bloomberg Ticker'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <InputComponent
            id={'s-and-p'}
            data-testid={'s-and-p'}
            value={stockDraft?.snl_code || ''}
            onChange={(e) =>
              setStockDraft({ ...(stockDraft as IStock), snl_code: e.currentTarget.value })
            }
            disabled={!editable}
            label={'S&P'}
            isWhite
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ paddingTop: 35 }}>
        <Grid item xs={3}>
          <AutocompleteComponent
            id="company-autocomplete"
            data-testid="company-autocomplete"
            options={(companies.data?.result as never[]) || []}
            getOptionLabel={(option: ICompany) => option?.name}
            values={stockDraft?.company || null}
            onChange={handleCompanyChange}
            disabled={!editable}
            label={'Company name'}
            isWhite
          />
        </Grid>
        <Grid item xs={2}>
          <InputComponent
            id={'icb-industry-code'}
            data-testid={'icb-industry-code'}
            type="number"
            value={stockDraft?.icb_industry_code ?? ''}
            onChange={(e) =>
              setStockDraft({
                ...(stockDraft as IStock),
                icb_industry_code: parseInt(e.currentTarget.value)
              })
            }
            disabled={!editable}
            label={'ICB Industry Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={2}>
          <InputComponent
            id={'icb-supersector-code'}
            data-testid={'icb-supersector-code'}
            type="number"
            value={stockDraft?.icb_supersector_code ?? ''}
            onChange={(e) =>
              setStockDraft({
                ...(stockDraft as IStock),
                icb_supersector_code: parseInt(e.currentTarget.value)
              })
            }
            disabled={!editable}
            label={'ICB Supersector Code'}
            isWhite
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteComponent
            id="subsector-autocomplete"
            data-testid="subsector-autocomplete"
            options={(sectors.data?.result as never[]) || []}
            getOptionLabel={(option: ISector) => option?.name}
            values={stockDraft?.subsector || null}
            onChange={(_, newValue) =>
              setStockDraft({ ...(stockDraft as IStock), subsector: newValue })
            }
            disabled={!editable}
            label={'Subsector'}
            isWhite
          />
        </Grid>
        <Grid item xs={1}>
          <FormControlLabel
            control={
              <Switch
                id="reit"
                disableRipple
                disabled={!editable}
                lang="reit"
                sx={{ mt: '2px' }}
                onChange={() =>
                  setStockDraft({ ...(stockDraft as IStock), is_reit: !stockDraft?.is_reit })
                }
                checked={stockDraft?.is_reit || false}
              />
            }
            label="REIT"
            labelPlacement="start"
            sx={{ ml: 0, mt: '5px' }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default StockDetails;
